import { type FC, useCallback, useState } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';
import {
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    trackEvent,
} from 'dibs-tracking';
import dibsCss from 'dibs-css';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import {
    BuyerProductTile,
    SRC_SET_SIZES_TILE,
    TILE_SIZES,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper.js';

import {
    trackUserRecommendationsItemClick,
    trackUserRecommendationsItemImpression,
} from './userRecommendationsTracking.ts';

import { type UserRecommendationsRecommendationTile_viewer$key } from './__generated__/UserRecommendationsRecommendationTile_viewer.graphql.ts';
import { type UserRecommendationsRecommendationTile_user$key } from './__generated__/UserRecommendationsRecommendationTile_user.graphql.ts';
import { type UserRecommendationsRecommendationTile_item$key } from './__generated__/UserRecommendationsRecommendationTile_item.graphql.ts';

const { EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const { STEP_INTERACTION_ITEM_ADDED, STEP_INTERACTION_ITEM_REMOVED } = stepInteractionConstants;

const viewerFragment = graphql`
    fragment UserRecommendationsRecommendationTile_viewer on Viewer
    @argumentDefinitions(
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
    ) {
        ...BuyerProductTile_viewer
        ...ItemHeartWrapper_viewer
            @arguments(userIds: [$userId], loadPortfolioData: $fetchFavorites)
    }
`;

const userFragment = graphql`
    fragment UserRecommendationsRecommendationTile_user on User {
        serviceId
        ...BuyerProductTile_user @arguments(showSeller: false)
    }
`;

const itemFragment = graphql`
    fragment UserRecommendationsRecommendationTile_item on Item
    @argumentDefinitions(
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        priceBookName: { type: "String" }
    ) {
        serviceId
        title
        browseUrl
        categoryCode
        ecommerceTrackingParams
        seller {
            serviceId
        }
        ...ItemHeartWrapper_item @arguments(userId: $userId, loadPortfolioData: $fetchFavorites)
        ...BuyerProductTile_item
            @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: false)
    }
`;

type UserRecommendationsRecommendationTileProps = {
    item: UserRecommendationsRecommendationTile_item$key;
    viewer: UserRecommendationsRecommendationTile_viewer$key;
    user: UserRecommendationsRecommendationTile_user$key | null | undefined;
    index: number;
    itemsPerRow: number;
    isMobile: boolean;
    trackingTrigger: string;
    dataTn?: string;
    currency: string;
};

export const UserRecommendationsRecommendationTile: FC<
    UserRecommendationsRecommendationTileProps
> = ({
    item: itemRef,
    viewer: viewerRef,
    user: userRef,
    index,
    itemsPerRow,
    isMobile,
    trackingTrigger,
    dataTn,
    currency,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const [isLoginFlowInProgress, setIsLoginFlowInProgress] = useState(false);

    const onProductClick = useCallback(() => {
        trackUserRecommendationsItemClick({ item, index, trackingTrigger });
    }, [item, index, trackingTrigger]);

    const onProductImpression = useCallback(() => {
        trackUserRecommendationsItemImpression({ item, index, trackingTrigger });
    }, [item, index, trackingTrigger]);

    const heartAuthModalShow = useCallback(() => {
        authModalLoader
            ?.show({
                flow: authModalLoader?.constants?.SAVE_ITEM_FLOW,
            })
            .then(() => {
                setIsLoginFlowInProgress(true);
            });
    }, []);

    const handleFavorited = useCallback(
        ({ type }: { type: 'favorited' | 'unFavorited' }) =>
            (): void => {
                const step_interaction_name =
                    type === 'favorited'
                        ? STEP_INTERACTION_ITEM_ADDED
                        : STEP_INTERACTION_ITEM_REMOVED;

                trackEvent(
                    {
                        action: step_interaction_name,
                        label: trackingTrigger,
                        category: INTERACTION_TYPE_ITEM_FAVORITING,
                        isInteractiveEvent: true,
                        additional: { itemId: item.serviceId },
                        eventName: EVENT_WISHLIST,
                        interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
                        step_interaction_name,
                        trigger: trackingTrigger,
                    },
                    null
                );
            },
        [item.serviceId, trackingTrigger]
    );

    const theme = isMobile ? 'circle-frosted-small' : 'circle-frosted-medium';

    return (
        <div
            data-tn={dataTn ? `${dataTn}-tile-${index}` : undefined}
            className={classnames(dibsCss.minW0, dibsCss.hFull)}
        >
            <BuyerProductTile
                item={item}
                viewer={viewer}
                itemSearch={null}
                user={user}
                afterDisplay
                currency={currency}
                imageBackgroundColor="moonStone"
                index={index}
                itemsPerRow={itemsPerRow}
                size={isMobile ? TILE_SIZES.small : TILE_SIZES.medium}
                srcSet={{ sizes: SRC_SET_SIZES_TILE }}
                showMeasurements={false}
                showMskuSwatch={false}
                showQuickViewButton={false}
                showPrice
                showShippingCost
                onClick={onProductClick}
                onContentVisible={onProductImpression}
                favoritesComponent={
                    <div
                        className={classnames(
                            dibsCss.absolute,
                            dibsCss.top0,
                            dibsCss.right0,
                            dibsCss.zBump
                        )}
                    >
                        <div
                            className={classnames(
                                dibsCss.relative,
                                dibsCss.ptXxsmall,
                                dibsCss.prXxsmall
                            )}
                        >
                            <ItemHeartWrapper
                                theme={theme}
                                itemId={item.serviceId}
                                userId={user?.serviceId}
                                viewer={viewer}
                                item={item}
                                authModalLoader={authModalLoader}
                                authModalShow={heartAuthModalShow}
                                onFavorited={handleFavorited({ type: 'favorited' })}
                                onUnFavorited={handleFavorited({ type: 'unFavorited' })}
                                isLoginFlowInProgress={isLoginFlowInProgress}
                            />
                        </div>
                    </div>
                }
            />
        </div>
    );
};
